import { tr } from "./i18n";
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { API_BASE, api } from "./api";
import { useAuth } from "./auth";

const ChatContext = createContext(null);

/** Web Audio API notification chime (no bundled asset). */
function playChime(volume = 0.35) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return false;
    const ctx = window.__chatAudioCtx || (window.__chatAudioCtx = new AC());
    if (ctx.state === "suspended") ctx.resume();

    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    gain.connect(ctx.destination);

    const o1 = ctx.createOscillator();
    o1.type = "sine";
    o1.frequency.setValueAtTime(880, now);
    o1.frequency.exponentialRampToValueAtTime(660, now + 0.28);
    o1.connect(gain);
    o1.start(now);
    o1.stop(now + 0.5);

    const o2 = ctx.createOscillator();
    o2.type = "sine";
    o2.frequency.setValueAtTime(1320, now + 0.1);
    o2.frequency.exponentialRampToValueAtTime(990, now + 0.32);
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0, now + 0.1);
    g2.gain.linearRampToValueAtTime(volume * 0.5, now + 0.12);
    g2.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    o2.connect(g2);
    g2.connect(ctx.destination);
    o2.start(now + 0.1);
    o2.stop(now + 0.5);
    return true;
  } catch {
    return false;
  }
}

export function ChatProvider({ children }) {
  const { user, updateProfile } = useAuth();
  const [socketReady, setSocketReady] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [lastEvent, setLastEvent] = useState(null); // for consumers to react
  const [counts, setCounts] = useState({ unassigned: 0, mine: 0, open: 0 });
  const wsRef = useRef(null);
  const backoffRef = useRef(1000);
  const lastSoundAt = useRef(0);
  const originalTitleRef = useRef(document.title);

  // Unlock audio on first user gesture
  useEffect(() => {
    if (audioUnlocked) return;
    const unlock = () => {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = window.__chatAudioCtx || (window.__chatAudioCtx = new AC());
        if (ctx.state === "suspended") ctx.resume().catch(() => {});
      }
      setAudioUnlocked(true);
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
    window.addEventListener("click", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    window.addEventListener("touchstart", unlock, { once: true });
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, [audioUnlocked]);

  // Restore sound preference from profile
  useEffect(() => {
    if (user && user !== false) setSoundOn(user.chat_sound !== false);
  }, [user?.id]);

  const canUseChat = !!user && user !== false && (user.role === "admin" || user.permissions?.chat?.view);

  const chime = useCallback(() => {
    if (!soundOn) return false;
    const now = Date.now();
    if (now - lastSoundAt.current < 3000) return false;
    lastSoundAt.current = now;
    return playChime();
  }, [soundOn]);

  const refreshCounts = useCallback(async () => {
    if (!canUseChat) return;
    try {
      const { data } = await api.get("/chat/conversations", { params: { scope: "all", user_id: user.id } });
      setCounts(data.counts || {});
      const total = (data.counts?.unassigned || 0) + (data.counts?.mine || 0);
      document.title = total > 0 ? `(${total}) ${originalTitleRef.current}` : originalTitleRef.current;
    } catch {}
  }, [canUseChat, user?.id]);

  useEffect(() => {
    if (!canUseChat) return;
    refreshCounts();
    const t = setInterval(refreshCounts, 30000);
    return () => clearInterval(t);
  }, [canUseChat, refreshCounts]);

  // WebSocket lifecycle
  useEffect(() => {
    if (!canUseChat) return;
    let closed = false;
    let hbInterval;

    const connect = () => {
      const token = localStorage.getItem("access_token") || "";
      // Prefer same-origin WebSocket (works behind any reverse proxy / custom
      // domain without a rebuild). If API_BASE is absolute (split-host mode)
      // fall back to it, otherwise derive from window.location.
      let wsBase;
      if (/^https?:\/\//i.test(API_BASE)) {
        wsBase = API_BASE.replace(/^http/i, "ws");
      } else {
        const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
        wsBase = `${proto}//${window.location.host}/api`;
      }
      const wsUrl = `${wsBase}/chat/ws/agent?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setSocketReady(true);
        backoffRef.current = 1000;
        hbInterval = setInterval(() => {
          try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
        }, 25000);
      };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "message" && data.message?.sender_type === "visitor") {
            const isMine = data.assigned_to === user.id || data.conversation_status === "unassigned";
            if (isMine) {
              chime();
              toast(data.message.sender_name || tr("x.visitor_2"), {
                description: data.message.text?.slice(0, 80),
              });
              // Desktop notification (only if tab hidden and permission granted)
              if (document.hidden && "Notification" in window && Notification.permission === "granted") {
                new Notification(tr("x.new_chat_message"), { body: data.message.text?.slice(0, 100) });
              }
            }
            refreshCounts();
          } else if (data.type === "handoff") {
            chime();
            toast.info(tr("x.a_visitor_is_asking_for_a_human_agent"));
            refreshCounts();
          } else if (data.type === "conversation_update") {
            refreshCounts();
          }
          setLastEvent(data);
        } catch {}
      };
      ws.onclose = () => {
        setSocketReady(false);
        if (hbInterval) clearInterval(hbInterval);
        if (closed) return;
        const b = Math.min(backoffRef.current, 30000);
        setTimeout(connect, b);
        backoffRef.current = b * 2;
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };

    connect();
    return () => {
      closed = true;
      if (hbInterval) clearInterval(hbInterval);
      try { wsRef.current?.close(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUseChat]);

  const testSound = () => {
    const ok = playChime(0.4);
    if (!ok) toast.error(tr("x.audio_is_blocked_by_the_browser_click_an"));
  };

  const toggleSound = async () => {
    const next = !soundOn;
    setSoundOn(next);
    try { await updateProfile({ chat_sound: next }); } catch {}
  };

  const requestDesktopNotifs = async () => {
    if (!("Notification" in window)) return toast.error(tr("x.desktop_notifications_unsupported"));
    const res = await Notification.requestPermission();
    if (res === "granted") toast.success(tr("x.desktop_notifications_enabled"));
    else toast.error(tr("x.permission_denied"));
  };

  return (
    <ChatContext.Provider
      value={{
        socketReady,
        audioUnlocked,
        soundOn,
        toggleSound,
        chime: () => { if (soundOn) playChime(); },
        testSound,
        requestDesktopNotifs,
        counts,
        refreshCounts,
        lastEvent,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => useContext(ChatContext);
